/**
 * Pre-Approval Consumer Grouping Utility
 * Groups pre-approvals by contact ID or borrower email to reduce dashboard clutter
 */

import type { PreApprovalListItem } from '@/types/pre-approval';

export interface ConsumerGroup {
  /** Unique identifier (contact ID, email, or pre-approval ID) */
  consumerKey: string;
  /** Borrower full name */
  consumerName: string;
  /** Borrower email address */
  consumerEmail: string;
  /** Most recent pre-approval for this consumer */
  latestPreApproval: PreApprovalListItem;
  /** All pre-approvals for this consumer, sorted by date (newest first) */
  preApprovals: PreApprovalListItem[];
  /** Total number of pre-approvals */
  totalCount: number;
  /** Count of active (draft, sent, viewed, accepted) pre-approvals */
  activeCount: number;
  /** Count of expired pre-approvals */
  expiredCount: number;
}

/**
 * Groups pre-approvals by consumer (contact ID, then email, then standalone)
 * Returns groups sorted by latest pre-approval date (most recent first)
 */
export function groupPreApprovalsByConsumer(
  preApprovals: PreApprovalListItem[]
): ConsumerGroup[] {
  const groups = new Map<string, ConsumerGroup>();

  for (const pa of preApprovals) {
    // Build grouping key: prefer contactId, then email, then unique pre-approval ID
    let groupKey: string;

    if (pa.contactId) {
      // Contact-linked pre-approvals group by contact
      groupKey = `contact:${pa.contactId}`;
    } else if (pa.borrowerEmail?.trim()) {
      // Email-based grouping for legacy records
      groupKey = `email:${pa.borrowerEmail.toLowerCase().trim()}`;
    } else {
      // No contact or email - treat as standalone (use pre-approval ID)
      groupKey = `orphan:${pa.id}`;
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        consumerKey: groupKey,
        consumerName: pa.borrowerName || 'Unknown',
        consumerEmail: pa.borrowerEmail || '',
        latestPreApproval: pa,
        preApprovals: [],
        totalCount: 0,
        activeCount: 0,
        expiredCount: 0
      });
    }

    const group = groups.get(groupKey)!;
    group.preApprovals.push(pa);
    group.totalCount++;

    if (pa.status === 'expired') {
      group.expiredCount++;
    } else {
      group.activeCount++;
    }

    // Update latest if this one is newer
    const currentDate = new Date(pa.createdAt).getTime();
    const latestDate = new Date(group.latestPreApproval.createdAt).getTime();
    if (currentDate > latestDate) {
      group.latestPreApproval = pa;
      // Update consumer name from latest record
      group.consumerName = pa.borrowerName || group.consumerName;
    }
  }

  // Sort pre-approvals within each group by date (newest first)
  for (const group of groups.values()) {
    group.preApprovals.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  // Sort groups by latest pre-approval date (most recent first)
  return Array.from(groups.values()).sort((a, b) =>
    new Date(b.latestPreApproval.createdAt).getTime() -
    new Date(a.latestPreApproval.createdAt).getTime()
  );
}

/**
 * Filter consumer groups based on search query
 * Searches consumer name, email, and pre-approval amounts
 */
export function filterConsumerGroups(
  groups: ConsumerGroup[],
  searchQuery: string
): ConsumerGroup[] {
  if (!searchQuery.trim()) {
    return groups;
  }

  const query = searchQuery.toLowerCase().trim();

  return groups.filter(group => {
    // Check consumer name and email
    if (group.consumerName.toLowerCase().includes(query)) {
return true;
}
    if (group.consumerEmail.toLowerCase().includes(query)) {
return true;
}

    // Check if any pre-approval amount matches
    const amountQuery = query.replace(/[$,]/g, '');
    if (!isNaN(Number(amountQuery))) {
      const searchAmount = Number(amountQuery);
      return group.preApprovals.some(pa =>
        pa.preApprovalAmount?.toString().includes(amountQuery) ||
        pa.preApprovalAmount === searchAmount
      );
    }

    return false;
  });
}

/**
 * Filter consumer groups by status
 * Returns groups that have at least one pre-approval matching the status
 */
export function filterGroupsByStatus(
  groups: ConsumerGroup[],
  status: PreApprovalListItem['status'] | 'all'
): ConsumerGroup[] {
  if (status === 'all') {
    return groups;
  }

  return groups.filter(group =>
    group.preApprovals.some(pa => pa.status === status)
  ).map(group => ({
    ...group,
    // Optionally filter preApprovals within group to only show matching status
    preApprovals: group.preApprovals.filter(pa => pa.status === status)
  }));
}

/**
 * Get summary statistics for consumer groups
 */
export function getGroupingSummary(groups: ConsumerGroup[]) {
  return {
    totalConsumers: groups.length,
    totalPreApprovals: groups.reduce((sum, g) => sum + g.totalCount, 0),
    totalActive: groups.reduce((sum, g) => sum + g.activeCount, 0),
    totalExpired: groups.reduce((sum, g) => sum + g.expiredCount, 0),
    consumersWithMultiple: groups.filter(g => g.totalCount > 1).length
  };
}
